// conectagran/frontend/src/api/hooks/useAdskeeper.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

const BASE = '/adskeeper'

// ─── Conexão (por usuário) ──────────────────────────────────────────────────

export interface AdskeeperConnection {
  connected: boolean
  client_id?: string
  last_used_at?: string
}

export function useAdskeeperConnection() {
  return useQuery<AdskeeperConnection>({
    queryKey: ['adskeeper', 'connection'],
    queryFn: () => api.get<AdskeeperConnection>(`${BASE}/connection`),
  })
}

export function useSaveAdskeeperConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { client_id: string; token: string }) =>
      api.post<AdskeeperConnection>(`${BASE}/connection`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adskeeper'] }),
  })
}

export function useDeleteAdskeeperConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete(`${BASE}/connection`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adskeeper'] }),
  })
}

export interface AdskeeperMetrics {
  impressions: number
  clicks: number
  wages: number   // receita US$
  ctr: number     // %
  cpc: number     // centavos
  ecpm: number    // US$
}

export interface AdskeeperWidgetRow extends AdskeeperMetrics {
  widgetId: string
  name: string
}

export interface AdskeeperDomainRow extends AdskeeperMetrics {
  domain: string
}

export interface AdskeeperDayRow extends AdskeeperMetrics {
  date: string    // YYYY-MM-DD
}

export interface AdskeeperReport {
  range: { from: string; to: string }
  totals: AdskeeperMetrics
  byWidget: AdskeeperWidgetRow[]
  byDomain: AdskeeperDomainRow[]
  byDay: AdskeeperDayRow[]
}

export function useAdskeeperReport(params: { from: string; to: string; enabled?: boolean }) {
  return useQuery<AdskeeperReport>({
    queryKey: ['adskeeper', 'report', params.from, params.to],
    queryFn: () => {
      const qs = new URLSearchParams({ from: params.from, to: params.to })
      return api.get<AdskeeperReport>(`${BASE}/report?${qs}`)
    },
    enabled: (params.enabled ?? true) && !!params.from && !!params.to,
    staleTime: 60_000,
  })
}
